import { observer } from 'mobx-react';
import ApplyViewModel from './ApplyViewModel';
import useAsyncEffect from 'use-async-effect';
import { Steps } from './ApplyModels';
import { useParams } from 'react-router-dom';
import { Appl } from '../../../../core/Appl';
import { CirclePlus, Plus } from 'lucide-react';
import { Button } from '../../../../core/components/Index';

const BeneficiaryDesignationUnderstandingBeneficiaries = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.UnderstandingBeneficiaries, memberId!);
  }, []);

  return (
    <>
      <div className="space-y-6 max-w-4xl text-15px">
        <div>
          <h1 id="page-title" className="text-32px tracking--1percent text-neutral-800 font-semibold mb-6">Beneficiary Designation</h1>
          <p className="text-gray-700 font-medium text-22px mb-3">Understanding Beneficiaries</p>
          <p className="text-black text-15px tracking--1percent leading-26px">
            A beneficiary is the person, trust, or organization you choose to receive certain benefits after your passing. As part of the Online Retirement Application, you will need to designate beneficiaries for two types of benefits:
          </p>
          <p className='mt-4 mb-4'>
            <ol className="list-decimal pl-6 text-black text-15px tracking--1percent leading-26px">
              <li><b>Your Retirement Benefit</b> - Either an ongoing monthly payment (continuance), depending on the retirement option you select, or any refundable member contributions (if applicable) will be paid to this beneficiary.</li>
              <li><b>Your Burial Allowance</b> - A separate one-time payment of up to $4,000 to assist with funeral and burial expenses.</li>
            </ol>
          </p>
          <p className="text-gray-700 font-semibold text-18px mb-4">Beneficiaries for Your Retirement Benefit</p>
          <p className="text-black text-15px tracking--1percent leading-26px">
            Your retirement benefit may be paid out as either a monthly continuance or a lump-sum payment, based on the retirement option you select. Any refundable member contributions still held by SCERS at the time of your death will also be paid to your designated retirement benefit beneficiary(ies).
          </p>
          <p className='mt-2 mb-2'>
            <p className='mt-2 mb-4'>
              <ul className="list-disc pl-6 text-black text-15px tracking--1percent leading-26px">
                <li>
                  <b>Ongoing Monthly Payments</b> (Continuance Beneficiary)
                  <p>
                    <ul className="list-disc pl-6 text-black text-15px tracking--1percent leading-26px mb-4">
                      <li>If your selected retirement option provides a continuance (Unmodified, Option 2, 3, or 4), your designated beneficiary will receive a percentage of your monthly pension for their lifetime.</li>
                      <li>This beneficiary is typically a spouse, registered domestic partner, or another individual with an insurable interest in your life. Once you retire, the beneficiary designation for options 2, 3 or 4 cannot be changed.</li>
                    </ul>
                  </p>
                </li>
                <li>
                  <b>One-Time Lump-Sum Payment</b> (Lump-Sum Beneficiary)
                  <p>
                    <ul className="list-disc pl-6 text-black text-15px tracking--1percent leading-26px">
                      <li>If your retirement option does not provide a continuance (e.g., Option 1), your designated beneficiary(ies) will receive any remaining member contributions as a lump-sum payment.</li>
                      <li>You may name an individual, trust, or charitable organization as a beneficiary for any remaining contributions.</li>
                    </ul>
                  </p>
                </li>
              </ul>
            </p>
          </p>
          <p className="text-black text-15px tracking--1percent leading-26px">
            <b>Note:</b> If you are unmarried and select the Unmodified Allowance, you may name your minor child(ren) as your continuance beneficiary. You may also designate another individual with an insurable interest; however, this designation would apply <b>only for the purpose of receiving any refundable member contributions and would not provide a monthly continuance.</b>
          </p>
          <p className="text-black text-15px tracking--1percent leading-26px italic  mt-4">
            <b>Important:</b> If you are married or in a registered domestic partnership, your spouse/partner has legal rights to your retirement benefit. If they are not named as the primary beneficiary, they will need to provide written consent.
          </p>
          <p className="text-gray-700 font-semibold text-18px mt-4 mb-4">Beneficiaries for the Burial Allowance</p>
          <p className="text-black text-15px tracking--1percent leading-26px">
            SCERS provides a burial allowance of up to $4,000 to help cover funeral and burial expenses. This is a separate benefit from your retirement payout.
          </p>
          <p className="text-black text-15px tracking--1percent leading-26px mt-4">
            You may designate one or more beneficiaries to receive this allowance; they do not have to be the same as your retirement benefit beneficiaries.
          </p>
        </div>
      </div>
      <div className="flex space-x-4 mt-6">
        <Button title="Continue" rounded onClick={async () => await vm.onUnderstandingBeneficiariesStepCompletedAsync()} />
      </div>
    </>
  );
});

export default BeneficiaryDesignationUnderstandingBeneficiaries;
